/*
* === CompanySubscriptionPage.js (Strategic SaaS Management Upgrade) ===
*/
import React, { useEffect, useState } from 'react';
import { 
    Container, Typography, Box, Paper, Chip, IconButton, Dialog, 
    DialogTitle, DialogContent, DialogActions, Button, FormControl, 
    InputLabel, Select, MenuItem, TextField, Alert, Snackbar, CircularProgress,
    Menu, ListItemIcon, ListItemText, Divider
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AddBusinessIcon from '@mui/icons-material/AddBusiness';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SettingsIcon from '@mui/icons-material/Settings';
import EmailIcon from '@mui/icons-material/Email';
import BlockIcon from '@mui/icons-material/Block';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

// Tier Hierarchy for checking downgrades
const TIER_RANK = { 'INCUBATOR': 1, 'STARTER': 2, 'GROWTH': 3, 'ENTERPRISE': 4 };

const CompanySubscriptionPage = () => {
    const { token } = useAuth();
    const [companies, setCompanies] = useState([]);
    
    // Loading States
    const [loadingData, setLoadingData] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    
    // Modals & Menus
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
    const [activeRow, setActiveRow] = useState(null);
    
    // Forms
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [updateForm, setUpdateForm] = useState({ tier: 'STARTER', status: 'Active', expiresAt: '' });
    const [createForm, setCreateForm] = useState({ name: '', regNumber: '', tier: 'STARTER', adminEmail: '' });

    // Feedback
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
    const showMessage = (msg, type = 'success') => setSnackbar({ open: true, message: msg, severity: type });

    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

    // 1. FETCH DATA
    const fetchCompanies = async () => {
        setLoadingData(true);
        try {
            const res = await api.get('/admin/companies', { headers: { Authorization: `Bearer ${token}` } });
            setCompanies(res.data);
        } catch (err) {
            console.error("Fetch Error:", err);
            showMessage('Failed to load companies.', 'error');
        } finally {
            setLoadingData(false);
        }
    };

    useEffect(() => { fetchCompanies(); }, [token]);

    // 2. ACTION MENU HANDLERS
    const handleOpenActionMenu = (event, row) => {
        setActionMenuAnchorEl(event.currentTarget);
        setActiveRow(row);
    };
    
    const handleCloseActionMenu = () => {
        setActionMenuAnchorEl(null);
        setActiveRow(null);
    };

    // 3. OPEN EDIT MODAL
    const handleEditClick = () => {
        if (!activeRow) return;
        setSelectedCompany(activeRow);
        
        const currentTier = (activeRow.SubscriptionTier || 'STARTER').toUpperCase(); 
        
        setUpdateForm({
            tier: currentTier, 
            status: activeRow.SubscriptionStatus || 'Active',
            expiresAt: activeRow.SubscriptionExpiresAt ? activeRow.SubscriptionExpiresAt.split('T')[0] : ''
        });
        
        handleCloseActionMenu();
        setEditModalOpen(true);
    };

    // 4. QUICK SUSPEND (From Dropdown)
    const handleQuickSuspend = async () => {
        if (!activeRow) return;
        setIsSubmitting(true);
        try {
            await api.put(`/admin/companies/${activeRow.Id}/subscription`, {
                tier: activeRow.SubscriptionTier,
                status: 'Suspended',
                expiresAt: activeRow.SubscriptionExpiresAt
            }, { headers: { Authorization: `Bearer ${token}` } });
            
            showMessage(`${activeRow.CompanyName} has been suspended.`, 'warning');
            fetchCompanies();
        } catch (err) {
            showMessage('Failed to suspend company.', 'error');
        } finally {
            setIsSubmitting(false);
            handleCloseActionMenu();
        }
    };

    // 5. SAVE UPDATE 
    const handleSaveUpdate = async () => {
        if (!selectedCompany) return;
        setIsSubmitting(true);
        
        try {
            await api.put(`/admin/companies/${selectedCompany.Id}/subscription`, updateForm, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            setEditModalOpen(false);
            showMessage('Subscription updated successfully!', 'success');
            fetchCompanies();
        } catch (err) {
            showMessage(err.response?.data?.message || 'Failed to update subscription.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    // 6. CREATE COMPANY
    const handleCreateCompany = async () => {
        if (!createForm.name || !createForm.regNumber) {
            showMessage('Company Name and Reg Number are required.', 'warning');
            return;
        }
        setIsSubmitting(true);
        try {
            await api.post('/admin/companies', createForm, { headers: { Authorization: `Bearer ${token}` } });
            setCreateModalOpen(false);
            setCreateForm({ name: '', regNumber: '', tier: 'STARTER', adminEmail: '' }); 
            showMessage('Company created successfully!', 'success');
            fetchCompanies();
        } catch (err) {
            showMessage(err.response?.data?.message || 'Failed to create company.', 'error');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDeleteCompanyClick = () => {
    setDeleteDialogOpen(true);
    setActionMenuAnchorEl(null); // Close the dropdown menu
    };

    const handleConfirmPurge = async () => {
        if (!activeRow) return;
        setIsSubmitting(true);
        try {
            await api.delete(`/admin/companies/${activeRow.Id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            showMessage(`${activeRow.CompanyName} has been permanently deleted.`, 'success');
            setDeleteDialogOpen(false);
            fetchCompanies(); // Refresh the grid data
        } catch (err) {
            showMessage(err.response?.data?.message || 'Failed to delete company.', 'error');
        } finally {
            setIsSubmitting(false);
            setActiveRow(null);
        }
    };

    // UX Logic: Check if they are downgrading the tier
    const isDowngrading = selectedCompany && 
        TIER_RANK[updateForm.tier] < TIER_RANK[(selectedCompany.SubscriptionTier || 'STARTER').toUpperCase()];

    // --- DATAGRID COLUMNS ---
    const columns = [
        { field: 'CompanyName', headerName: 'Company Name', flex: 1, minWidth: 200 },
        { 
            field: 'SubscriptionTier', headerName: 'Tier', width: 130,
            renderCell: (params) => {
                const tier = (params.value || '').toUpperCase();
                let color = 'default';
                if (tier === 'STARTER') color = 'info';
                if (tier === 'GROWTH') color = 'warning';
                if (tier === 'ENTERPRISE') color = 'secondary';
                return <Chip label={tier} color={color} size="small" sx={{ fontWeight: 'bold' }} />;
            }
        },
        { 
            field: 'SubscriptionStatus', headerName: 'Status', width: 130,
            renderCell: (params) => {
                const isActive = params.value === 'Active';
                return (
                    <Chip 
                        icon={isActive ? <CheckCircleIcon/> : <CancelIcon/>}
                        label={params.value} 
                        color={isActive ? 'success' : 'error'} 
                        variant="outlined" size="small" 
                    />
                );
            }
        },
        { 
            field: 'SubscriptionExpiresAt', headerName: 'Expires On', width: 140,
            renderCell: (params) => {
                if (!params.value) return <Typography variant="body2" color="text.secondary">Auto-Renew</Typography>;
                const expDate = new Date(params.value);
                const isExpiringSoon = (expDate - new Date()) / (1000 * 60 * 60 * 24) <= 30; // 30 days
                return (
                    <Typography variant="body2" color={isExpiringSoon ? 'error.main' : 'text.primary'} fontWeight={isExpiringSoon ? 'bold' : 'normal'}>
                        {expDate.toLocaleDateString()}
                    </Typography>
                );
            }
        },
        { field: 'AdminEmail', headerName: 'Primary Contact', flex: 1, minWidth: 200 },
        { field: 'adminCount', headerName: 'Admins', width: 80, align: 'center' },
        {
            field: 'actions', headerName: 'Actions', width: 80, sortable: false, align: 'center',
            renderCell: (params) => (
                <IconButton color="primary" onClick={(e) => handleOpenActionMenu(e, params.row)}>
                    <MoreVertIcon />
                </IconButton>
            )
        }
    ];

    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" fontWeight="bold">Subscription Management</Typography>
                <Button variant="contained" startIcon={<AddBusinessIcon />} onClick={() => setCreateModalOpen(true)}>
                    Add Company
                </Button>
            </Box>

            <Paper sx={{ height: 600, width: '100%', mb: 3, boxShadow: 3 }}>
                <DataGrid
                    rows={companies}
                    columns={columns}
                    getRowId={(row) => row.Id}
                    loading={loadingData}
                    pageSize={10}
                    slots={{ toolbar: GridToolbar }} // 🚨 Instantly adds Search/Export/Filters!
                    slotProps={{ toolbar: { showQuickFilter: true } }}
                    sx={{ '& .MuiDataGrid-toolbarContainer': { p: 2, borderBottom: '1px solid #eee' } }}
                />
            </Paper>

            {/* --- ACTION DROPDOWN MENU --- */}
            <Menu
                anchorEl={actionMenuAnchorEl}
                open={Boolean(actionMenuAnchorEl)}
                onClose={handleCloseActionMenu}
                PaperProps={{ sx: { width: 220 } }}
            >
                <MenuItem onClick={handleEditClick}>
                    <ListItemIcon><SettingsIcon fontSize="small" color="primary" /></ListItemIcon>
                    <ListItemText>Manage Subscription</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => {
                    window.location.href = `mailto:${activeRow?.AdminEmail}?subject=BrightSparc Subscription`;
                    handleCloseActionMenu();
                }}>
                    <ListItemIcon><EmailIcon fontSize="small" /></ListItemIcon>
                    <ListItemText>Email Primary Admin</ListItemText>
                </MenuItem>
                <Divider />
                <MenuItem onClick={handleQuickSuspend} disabled={activeRow?.SubscriptionStatus === 'Suspended'}>
                    <ListItemIcon><BlockIcon fontSize="small" color="error" /></ListItemIcon>
                    <ListItemText sx={{ color: 'error.main' }}>Suspend Account</ListItemText>
                </MenuItem>
                <Divider />
            {/* 🚨 NEW: Delete option inside the menu */}
            <MenuItem onClick={handleDeleteCompanyClick}>
                <ListItemIcon><CancelIcon fontSize="small" color="error" /></ListItemIcon>
                <ListItemText sx={{ color: 'error.main', fontWeight: 'bold' }}>Delete Company</ListItemText>
            </MenuItem>
            </Menu>

            {/* --- MODAL 1: EDIT SUBSCRIPTION --- */}
            <Dialog open={editModalOpen} onClose={() => !isSubmitting && setEditModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ bgcolor: 'background.default', borderBottom: 1, borderColor: 'divider' }}>
                    Manage Configuration: {selectedCompany?.CompanyName}
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, mt: 2 }}>
                        
                        {/* Section 1: Billing & Tier */}
                        <Box>
                            <Typography variant="subtitle2" color="primary" gutterBottom textTransform="uppercase" fontWeight="bold">Billing & Plan</Typography>
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel>Subscription Tier</InputLabel>
                                <Select value={updateForm.tier} label="Subscription Tier" onChange={(e) => setUpdateForm({...updateForm, tier: e.target.value})}>
                                    <MenuItem value="INCUBATOR">INCUBATOR (Free/Trial)</MenuItem>
                                    <MenuItem value="STARTER">STARTER</MenuItem>
                                    <MenuItem value="GROWTH">GROWTH</MenuItem> 
                                    <MenuItem value="ENTERPRISE">ENTERPRISE</MenuItem>
                                </Select>
                            </FormControl>

                            {isDowngrading && (
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    You are downgrading this client. Ensure their current active funds and admin count do not exceed the limits of the lower tier.
                                </Alert>
                            )}

                            <TextField
                                fullWidth label="Expiry Date" type="date" InputLabelProps={{ shrink: true }}
                                value={updateForm.expiresAt} onChange={(e) => setUpdateForm({...updateForm, expiresAt: e.target.value})}
                                helperText="Leave blank for auto-renewing subscriptions."
                            />
                        </Box>

                        <Divider />

                        {/* Section 2: Operational Status */}
                        <Box>
                            <Typography variant="subtitle2" color="primary" gutterBottom textTransform="uppercase" fontWeight="bold">Account Status</Typography>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select value={updateForm.status} label="Status" onChange={(e) => setUpdateForm({...updateForm, status: e.target.value})}>
                                    <MenuItem value="Active">Active (Live)</MenuItem>
                                    <MenuItem value="Trial">Trial Period</MenuItem>
                                    <MenuItem value="Suspended">Suspended (Blocked Access)</MenuItem>
                                    <MenuItem value="Cancelled">Cancelled</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>
                    <Button onClick={() => setEditModalOpen(false)} disabled={isSubmitting} color="inherit">Cancel</Button>
                    <Button onClick={handleSaveUpdate} variant="contained" disabled={isSubmitting} startIcon={isSubmitting && <CircularProgress size={20} color="inherit" />}>
                        {isSubmitting ? 'Saving Changes...' : 'Save Configuration'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* --- MODAL 2: CREATE COMPANY --- */}
            <Dialog open={createModalOpen} onClose={() => !isSubmitting && setCreateModalOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Onboard New Company</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField label="Company Name" fullWidth required value={createForm.name} onChange={(e) => setCreateForm({...createForm, name: e.target.value})} />
                        <TextField label="Registration Number" fullWidth required value={createForm.regNumber} onChange={(e) => setCreateForm({...createForm, regNumber: e.target.value})} />
                        <FormControl fullWidth>
                            <InputLabel>Initial Tier</InputLabel>
                            <Select value={createForm.tier} label="Initial Tier" onChange={(e) => setCreateForm({...createForm, tier: e.target.value})}>
                                <MenuItem value="INCUBATOR">INCUBATOR</MenuItem>
                                <MenuItem value="STARTER">STARTER</MenuItem>
                                <MenuItem value="GROWTH">GROWTH</MenuItem> 
                                <MenuItem value="ENTERPRISE">ENTERPRISE</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField label="Primary Admin Email" fullWidth placeholder="e.g. admin@company.com" value={createForm.adminEmail} onChange={(e) => setCreateForm({...createForm, adminEmail: e.target.value})} />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCreateModalOpen(false)} disabled={isSubmitting}>Cancel</Button>
                    <Button onClick={handleCreateCompany} variant="contained" color="success" disabled={isSubmitting}>
                        {isSubmitting ? 'Creating...' : 'Create Company'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* --- MODAL 3: PURGE CONFIRMATION --- */}
            <Dialog open={deleteDialogOpen} onClose={() => !isSubmitting && setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Permanent Deletion</DialogTitle>
                <DialogContent>
                    <Typography variant="body1">
                        Are you sure you want to completely delete <strong>{activeRow?.CompanyName}</strong>?
                    </Typography>
                    <Typography variant="body2" color="error.main" sx={{ mt: 1, fontWeight: 'bold' }}>
                        Warning: This action will permanently delete this profile and any linked user records. This cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={isSubmitting} color="inherit">Cancel</Button>
                    <Button onClick={handleConfirmPurge} variant="contained" color="error" disabled={isSubmitting}>
                        {isSubmitting ? 'Purging Record...' : 'Confirm Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
            </Snackbar>
        </Container>
    );
};

export default CompanySubscriptionPage;